import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Colors from "../utils/colors";
import Typography from '@material-ui/core/Typography';
import ImpactList from '../components/impact/ImpactList';
import ValueTypeSelection from '../components/impact/ValueTypeSelection';
import ValuesSummaryCard from '../components/impact/ValuesSummaryCard';
import { getValuesApprovalBtn } from '../components/initiative/LxApprovalButtons';
import CreateButton from '../components/CreateButton';
import { BodyText, SubsectionHeader } from '../components/Typography';

import { ErrorContext, NotificationContext } from '../utils/constants';
import apiFetch, { getData } from '../utils/apiUtils';

import { canEditInitiative} from '../utils/initiatives';

const useStyles = makeStyles(() => ({
    header: {
        marginBottom: '5px'
    },
    main: {
        width: '100%'
    },
    approvalButton: {
        display: 'flex',
        justifyContent: 'center',
        paddingTop: '5rem',
        paddingBottom: '5rem'
    },
    inline: {
        margin: '1rem',
    }
}));

export default function InitiativeValues() {
    const classes = useStyles();
    const history = useHistory();
    const { id: initiativeId } = useParams();
    const { triggerError } = useContext(ErrorContext);
    const [ values, setValues ] = useState([]);
    const [ valueState, setValueState ] = useState('');
    const [ activeImpact, setActiveImpact ] = useState('');
    const [ initiative, setInitiative ] = useState({ _links: {} });
    const [ initiativeTeam, setInitiativeTeam ] = useState([]);
    const [ notifySummaryOfChange, setNotifySummaryOfChange ] = useState(0);
    const notification = useContext(NotificationContext);

    const impactListCallback = (payload) => {
        setValues(payload.values);
    };

    useEffect(() => {
        document.title = 'Rise by TransAlta Initiative Values';
    }, []);

    const onClickImpact = (impact) => setActiveImpact((impact === activeImpact) ? '' : impact);

    const refreshValues = () => {
        getData(`/initiatives/${initiativeId}`, setInitiative, onApiError);
        getData(`/initiatives/${initiativeId}/team`, setInitiativeTeam, onApiError);
        getData(`/initiatives/${initiativeId}/values`, impactListCallback, onApiError);
    }

    const handleImpactError = (error) => {
        if (error.status === 404) {
            history.push('/ui/errors/error404');
        } else {
            onApiError();
        }
    };

    const onApiError = () => triggerError({
        title: 'Communication Error',
        description: 'We were unable to read your impact data from the server. Please try again.',
        onRetry: () => window.location.reload()
    });

    const onCreateSuccess = (impact) => {
        setValueState('');
        setActiveImpact(impact.id);
    };

    useEffect(() => {
        getData(`/initiatives/${initiativeId}`, setInitiative, handleImpactError);
        getData(`/initiatives/${initiativeId}/team`, setInitiativeTeam, handleImpactError);
    }, [ initiativeId ]);

    useEffect(() => {
        if (initiative && initiative.id) {
            getData(`/initiatives/${initiativeId}/values`, impactListCallback, onApiError);
        }
    }, [ valueState, activeImpact, initiativeId, initiative ]);

    useEffect(() => {
        const isSupportedNotification = (notification && notification.initiativeId === initiativeId);

        if (!!isSupportedNotification) {
            getData(`/initiatives/${initiativeId}/values`, impactListCallback, onApiError);
            getData(`/initiatives/${initiativeId}`, setInitiative, handleImpactError);
            getData(`/initiatives/${initiativeId}/team`, setInitiativeTeam, handleImpactError);
        }
    }, [notification]);

    const createImpact = () => {
        setValueState('untyped');
    };

    const onCreateCancel = () => {
        setValueState('');
    };

    const approvalHeader = () => {
        return !!initiative.hasValues && (
          <>
            <SubsectionHeader>
              Value Tracking Checkpoint
            </SubsectionHeader>
            <BodyText>
              When you’ve completed the Value Tracking section, have it validated before proceeding.
            </BodyText>
          </>
        );
      };

    const hasCashFlowImpact = () => {
        return !!initiative.cashFlow;
    };
    const disabled = () => {
        return !canEditInitiative(initiativeTeam);
    };

    const handeValuesChanged = () => {
        setNotifySummaryOfChange(notifySummaryOfChange + 1);
    };

    return (
        <main className={classes.main}>
            <h1 className={classes.header}>
                Value Tracking
            </h1>
            <Typography>
                Measuring the value of this initiative.
            </Typography>
            <ImpactList
                values={values}
                activeImpact={activeImpact}
                onClickImpact={onClickImpact}
                refreshValues={refreshValues}
                disabled={disabled()}
                onValuesChanged={handeValuesChanged}
                targetDisabled={initiative.short_name === 'Deliver' || initiative.short_name === 'DeliverSubmitted' ||
                  initiative.short_name === 'Embed' || initiative.short_name === 'EmbedSubmitted'}
            />
            {!valueState && <CreateButton onClick={createImpact} disabled={disabled()}>
                Create Metric
            </CreateButton>}
            {valueState === 'untyped' && <ValueTypeSelection onCancel={onCreateCancel} onSuccess={onCreateSuccess} disabled={disabled()} />}
            <ValuesSummaryCard values={values} valuesChanged={notifySummaryOfChange}/>
            {approvalHeader()}
            {getValuesApprovalBtn(initiative, refreshValues)}
        </main>
    );
}
